import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { agentAPI } from '@/lib/api';

/**
 * ChangeNPNDialog — KR-only. Cascades the NPN change through every dependent
 * record (contracts, appointments, compensation, follow-ups, invitations,
 * linked-user). Every submission is audit-logged by the backend.
 */
export const ChangeNPNDialog = ({ open, onOpenChange, agent }) => {
  const navigate = useNavigate();
  const [newNpn, setNewNpn] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [pending, setPending] = React.useState(false);

  // Reset the form when the dialog closes so re-opening starts clean.
  React.useEffect(() => {
    if (!open) { setNewNpn(''); setReason(''); }
  }, [open]);

  const submit = async () => {
    const target = (newNpn || '').trim();
    if (!target || target === agent?.npn) {
      toast.error('Please enter a new NPN different from the current one');
      return;
    }
    setPending(true);
    try {
      const r = await agentAPI.changeNPN(agent.npn, target, reason || null);
      const c = r.data?.cascade || {};
      const total = Object.values(c).filter((v) => typeof v === 'number').reduce((a, b) => a + b, 0);
      toast.success(`NPN changed — ${total} related record${total === 1 ? '' : 's'} updated`);
      onOpenChange(false);
      navigate(`/agents/${target}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change NPN');
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-npn-dialog">
        <DialogHeader>
          <DialogTitle>Change NPN</DialogTitle>
          <DialogDescription>
            This will update the agent&apos;s NPN AND cascade the change to every
            contract, appointment, compensation record, follow-up, invitation,
            and linked-user reference. This action is audit-logged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">Current NPN</Label>
            <p className="font-mono text-sm">{agent?.npn}</p>
          </div>
          <div>
            <Label htmlFor="new-npn">New NPN *</Label>
            <Input
              id="new-npn"
              value={newNpn}
              onChange={(e) => setNewNpn(e.target.value)}
              placeholder="e.g. 20418772"
              data-testid="change-npn-input"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="npn-reason">Reason (recorded in the audit log)</Label>
            <Textarea
              id="npn-reason"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. NPN was mistyped during onboarding"
              data-testid="change-npn-reason"
            />
          </div>
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            ⚠ The new NPN must not already belong to another agent. If a conflict is
            detected we&apos;ll return a 409 with the message so you can pick a
            different value.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={pending || !newNpn.trim()}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="change-npn-confirm"
          >
            {pending ? 'Changing…' : 'Change NPN'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeNPNDialog;
